import { refreshAccessToken } from "@auth/refreshAccess"
import { authStorage } from "@auth/storage"
import { analytics } from "@lib/analytics"
import { apiErrorCode } from "@lib/apiError"
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios"

import { SESSION_EXPIRED_LOGIN_URL } from "@/constants/auth"
import { env } from "@/env"

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean; _startedAt?: number }

// Every request is bounded. Without a timeout axios waits indefinitely, so
// a dropped connection leaves a mutation spinner up until the tab closes.
// 30s matches the three other axios instances in this repo (portal,
// account-activation, admin-web). File transfers need a looser bound and
// pass TRANSFER_TIMEOUT_MS explicitly — six call sites in src/api/.
const DEFAULT_TIMEOUT_MS = 30_000
export const TRANSFER_TIMEOUT_MS = 120_000

export const apiClient = axios.create({
  baseURL: `${env.VITE_API_BASE_URL}/api`,
  headers: { "Content-Type": "application/json" },
  timeout: DEFAULT_TIMEOUT_MS,
})

// Attach access token on every request.
apiClient.interceptors.request.use((config) => {
  const token = authStorage.getAccess()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Read back by `reportFailure` to put a duration on a failed request — a
  // 30s timeout and a 40ms 500 are very different incidents.
  ;(config as RetryableRequest)._startedAt = Date.now()
  return config
})

// Endpoints whose 401 must reach the caller untouched — the login form needs
// to render its own error message, and the refresh endpoint is the refresh
// itself (intercepting it would loop).
const AUTH_ENDPOINTS = ["/auth/login", "/auth/refresh"]

let sessionEnding = false

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false
  return AUTH_ENDPOINTS.some((path) => url.endsWith(path))
}

/**
 * PRD-57 FR-14 — the one place a failed request is visible. Until this
 * existed, a 5xx, a network drop or a timeout fell straight through
 * `Promise.reject` with nothing recorded unless the call site happened to
 * log it, and most did not.
 *
 * Not reported:
 *  - cancelled requests (navigation away is not a failure);
 *  - a 401 from the login/refresh endpoints (wrong password — the backend
 *    reports that, attributed to the right professor).
 * The 401 that triggers a token refresh never reaches here at all: it is
 * replayed, and only the replay's outcome counts.
 */
function reportFailure(error: AxiosError, request: RetryableRequest) {
  if (axios.isCancel(error)) return
  const status = error.response?.status ?? null
  if (status === 401 && isAuthEndpoint(request.url)) return
  analytics.captureApiFailure({
    method: (request.method ?? "get").toUpperCase(),
    path: (request.url ?? "").split("?")[0] ?? "",
    status,
    code: apiErrorCode(error),
    isNetwork: !error.response && error.code !== "ECONNABORTED",
    isTimeout: error.code === "ECONNABORTED",
    durationMs: request._startedAt ? Date.now() - request._startedAt : null,
    isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
  })
}

// On 401, try to refresh once, then replay the original request.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequest | undefined
    if (!original) return Promise.reject(error)

    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint(original.url)) {
      original._retry = true
      // Shared with the realtime client (auth/refreshAccess.ts). Returns
      // null both when no refresh token exists and when the refresh
      // itself fails — for an API call either means the session is over.
      const access = await refreshAccessToken()
      if (!access) {
        // This hard redirect bypasses `AuthContext.logout()`, so without its
        // own event a session that DIED is indistinguishable in PostHog from
        // one still open. Queued before the redirect; posthog-js flushes its
        // queue with `sendBeacon` on unload.
        // Once per page: every request that 401'd in parallel lands here, and
        // each used to track its own expiry (four events for one session).
        if (!sessionEnding) {
          sessionEnding = true
          analytics.track("professor_session_expired")
          authStorage.clear()
          if (typeof window !== "undefined") window.location.href = SESSION_EXPIRED_LOGIN_URL
        }
        return Promise.reject(error)
      }
      original.headers.Authorization = `Bearer ${access}`
      return apiClient(original)
    }

    reportFailure(error, original)
    return Promise.reject(error)
  },
)
